import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { teamsAPI } from '../../../api/client';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export default function TeamsSection() {
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [upn, setUpn] = useState('');
  const [digestHour, setDigestHour] = useState(8);
  const [digestEmail, setDigestEmail] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await teamsAPI.getConnection();
      setConn(res.data);
      setUpn(res.data.teams_upn || '');
      setDigestHour(res.data.digest_hour ?? 8);
      setDigestEmail(res.data.digest_email || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load Teams settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await teamsAPI.updateSettings({
        teams_upn: upn,
        digest_hour: parseInt(digestHour),
        digest_email: digestEmail,
      });
      setConn(res.data);
      toast.success('Teams settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async () => {
    if (!conn) return;
    try {
      const res = await teamsAPI.updateSettings({ enabled: !conn.enabled });
      setConn(res.data);
      toast.success(res.data.enabled ? 'Teams monitoring enabled' : 'Teams monitoring disabled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const toggleDigest = async () => {
    if (!conn) return;
    try {
      const res = await teamsAPI.updateSettings({ digest_enabled: !conn.digest_enabled });
      setConn(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Azure status */}
      {!conn?.azure_configured ? (
        <div className="flex items-start gap-2 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
          <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Azure app credentials are not configured.</p>
            <p className="text-xs mt-0.5">
              Set <code>AZURE_CLIENT_ID</code>, <code>AZURE_CLIENT_SECRET</code>, and <code>AZURE_TENANT_ID</code> in the
              server <code>.env</code>, then restart the backend.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-green-200 bg-green-50 text-sm text-green-700 font-medium">
          ● Azure app credentials configured
        </div>
      )}

      {/* Monitoring settings card */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-5 flex items-center justify-between border-b border-slate-100">
          <div>
            <p className="font-semibold text-slate-800">Monitoring Settings</p>
            <p className="text-sm text-slate-500">Which account to watch and when to digest</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 font-medium">{conn?.enabled ? 'On' : 'Off'}</span>
            <button
              onClick={toggleEnabled}
              disabled={!conn?.teams_upn}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-40 ${
                conn?.enabled ? 'bg-primary-500' : 'bg-slate-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                conn?.enabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Teams email / UPN to monitor</label>
            <input
              type="email"
              value={upn}
              onChange={(e) => setUpn(e.target.value)}
              placeholder="you@company.com"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
            <p className="text-xs text-slate-400 mt-1">
              The Microsoft 365 account whose @mentions will be captured (across all chats and team channels).
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Daily digest hour</label>
              <select
                value={digestHour}
                onChange={(e) => setDigestHour(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{`${String(h).padStart(2, '0')}:00`}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Digest email <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <input
                type="email"
                value={digestEmail}
                onChange={(e) => setDigestEmail(e.target.value)}
                placeholder="defaults to your login email"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
          </div>

          <label className="flex items-center gap-3 cursor-pointer w-fit">
            <button
              type="button"
              onClick={toggleDigest}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                conn?.digest_enabled ? 'bg-primary-500' : 'bg-slate-300'
              }`}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                conn?.digest_enabled ? 'translate-x-4' : 'translate-x-0.5'
              }`} />
            </button>
            <span className="text-sm text-slate-700">Send daily digest</span>
          </label>

          {conn?.last_error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-100 text-xs text-red-600">
              <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span className="break-words">Last sync error: {conn.last_error}</span>
            </div>
          )}

          <div className="flex items-center justify-between pt-1">
            <p className="text-xs text-slate-400">
              {conn?.last_polled ? `Last checked: ${new Date(conn.last_polled).toLocaleString()}` : 'Not checked yet'}
            </p>
            <button
              onClick={saveSettings}
              disabled={saving}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
